// src/components/achievements/AchievementsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../services/firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { getAchievementDefinitions, getUserAchievements } from '../../services/firebase/firestoreService';
import './AchievementsPage.css';

const CATEGORIES = [
  { key: 'kheladu', labelEn: 'Athlete', labelMr: 'खेळाडू', color: '#007fff' },
  { key: 'vidyarthi', labelEn: 'Student', labelMr: 'विद्यार्थी', color: '#6366f1' },
  { key: 'tapasvi', labelEn: 'Disciplined', labelMr: 'तपस्वी', color: '#FF6B35' },
  { key: 'gaavkari', labelEn: 'Community', labelMr: 'गावकरी', color: '#eaa221' }
];

const CRITERIA_UNIT_MR = {
  totalFitnessDays: 'दिवस',
  totalSquats: 'स्क्वॅट्स',
  totalJumpingJacks: 'जंपिंग जॅक्स',
  totalLekhiDays: 'दिवस',
  totalTopicsCovered: 'विषय',
  grammarTopicsCovered: 'विषय',
  mathTopicsCovered: 'विषय',
  reasoningTopicsCovered: 'विषय',
  gkTopicsCovered: 'विषय',
  testsCompleted: 'चाचण्या',
  highestTestScore: 'गुण',
  unifiedStreak: 'दिवस',
  dualDays: 'दिवस',
  campsCompleted: 'कॅम्प',
  villageTotalPoints: 'गुण',
  userPointsContributed: 'गुण'
};

const CRITERIA_UNIT_EN = {
  totalFitnessDays: 'days',
  totalSquats: 'squats',
  totalJumpingJacks: 'jumping jacks',
  totalLekhiDays: 'days',
  totalTopicsCovered: 'topics',
  grammarTopicsCovered: 'topics',
  mathTopicsCovered: 'topics',
  reasoningTopicsCovered: 'topics',
  gkTopicsCovered: 'topics',
  testsCompleted: 'tests',
  highestTestScore: 'marks',
  unifiedStreak: 'days',
  dualDays: 'days',
  campsCompleted: 'camps',
  villageTotalPoints: 'points',
  userPointsContributed: 'points'
};

const toMarathiDigits = (num) => {
  const map = ['०','१','२','३','४','५','६','७','८','९'];
  return String(num).replace(/[0-9]/g, d => map[d]);
};

const TrophyIcon = ({ size = 32, white = false }) => (
  <img src="/trophy.svg" alt="trophy" width={size} height={size}
    style={{ filter: white ? 'brightness(0) invert(1)' : 'none', display: 'block' }} />
);

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  if (!text) return y;
  const words = text.split(' ');
  let line = '';
  let currentY = y;
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line.trim(), x, currentY);
      line = words[i] + ' ';
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line.trim(), x, currentY);
  return currentY + lineHeight;
};

const AchievementsPage = () => {
  const { currentUser, userData } = useAuth();
  const [language, setLanguage] = useState(() => localStorage.getItem('spocademy_language') || 'mr');
  const [achievements, setAchievements] = useState([]);
  const [unlockedIds, setUnlockedIds] = useState(new Set());
  const [categoryTemplates, setCategoryTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [cardImageUrl, setCardImageUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [shareFailedPrompt, setShareFailedPrompt] = useState(false);
  const canvasRef = useRef();

  const t = language === 'mr' ? {
    title: 'उपलब्धी',
    locked: 'बंद',
    download: 'डाउनलोड करा',
    share: 'शेअर करा',
    shareFailed: 'शेअर करता आलं नाही. डाउनलोड करायचं आहे का?',
    yes: 'हो',
    no: 'नाही',
    generating: 'तयार होत आहे...',
    downloadSuccess: 'फोटो डाउनलोड झाला! WhatsApp वर शेअर करा 🎉',
    close: 'बंद करा',
    noAchievements: 'अजून कोणती उपलब्धी नाही',
    unlocked: 'मिळवले!',
    lang: 'EN'
  } : {
    title: 'Achievements',
    locked: 'Locked',
    download: 'Download',
    share: 'Share',
    shareFailed: "Couldn't share. Download instead?",
    yes: 'Yes',
    no: 'No',
    generating: 'Generating...',
    downloadSuccess: 'Photo downloaded! Share on WhatsApp 🎉',
    close: 'Close',
    noAchievements: 'No achievements yet',
    unlocked: 'Unlocked!',
    lang: 'मराठी'
  };

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) return;
      try {
        const [defs, userAch, templatesSnap] = await Promise.all([
          getAchievementDefinitions(),
          getUserAchievements(currentUser.uid),
          getDocs(collection(db, 'achievementTemplates'))
        ]);
        setAchievements(defs);
        setUnlockedIds(new Set(userAch.map(a => a.achievementId)));
        const templates = {};
        templatesSnap.docs.forEach(d => {
          templates[d.id] = d.data().imageURL;
        });
        setCategoryTemplates(templates);
      } catch (e) {}
      finally { setLoading(false); }
    };
    load();
  }, [currentUser?.uid]);

  const generateCard = async (achievement) => {
    setGenerating(true);
    setCardImageUrl(null);
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = 1080;
      canvas.height = 1440;

      const templateURL = categoryTemplates[achievement.category];
      if (!templateURL) {
        setGenerating(false);
        return;
      }

      // Draw template background
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { ctx.drawImage(img, 0, 0, 1080, 1440); resolve(); };
        img.onerror = reject;
        img.src = templateURL;
      });

      // Draw user photo — circle centered at (540, 450), radius 230
      if (userData?.photoURL) {
        await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const cx = 540, cy = 450, r = 230;
            ctx.save();
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.clip();
            // Photo is already a clean square; draw its full size into the circle
            const side = Math.min(img.width, img.height);
            const sx = (img.width - side) / 2;
            const sy = (img.height - side) / 2;
            ctx.drawImage(img, sx, sy, side, side, cx - r, cy - r, r * 2, r * 2);
            ctx.restore();
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.strokeStyle = 'white';
            ctx.lineWidth = 12;
            ctx.stroke();
            resolve();
          };
          img.onerror = resolve;
          img.src = userData.photoURL;
        });
      }

      // Full name — below photo
      const firstName = userData?.firstName || userData?.name?.split(' ')[0] || '';
      const lastName = userData?.lastName || userData?.name?.split(' ').slice(1).join(' ') || '';
      const fullName = `${firstName} ${lastName}`.trim();
      ctx.fillStyle = 'white';
      ctx.font = 'bold 72px Arial';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 8;
      ctx.fillText(fullName, 540, 790);
      ctx.shadowBlur = 0;

      // Achievement title — language aware
      const title = achievement.title?.[language] || achievement.title?.mr || achievement.title?.en || '';
      ctx.font = 'bold 60px Arial';
      ctx.fillStyle = 'white';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 6;
      wrapText(ctx, title, 540, 1080, 900, 72);
      ctx.shadowBlur = 0;

      // Achievement description — language aware
      const description = achievement.description?.[language] || achievement.description?.mr || achievement.description?.en || '';
      ctx.font = '50px Arial';
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = 4;
      wrapText(ctx, description, 540, 1200, 700, 64);
      ctx.shadowBlur = 0;

      setCardImageUrl(canvas.toDataURL('image/png'));
    } catch (e) {}
    setGenerating(false);
  };

  const handleAchievementTap = async (achievement) => {
    if (!unlockedIds.has(achievement.id)) return;
    setSelectedAchievement(achievement);
    setDownloadSuccess(false);
    await generateCard(achievement);
  };

  const handleDownload = () => {
    if (!cardImageUrl) return;
    const link = document.createElement('a');
    link.href = cardImageUrl;
    link.download = 'spocademy_achievement.png';
    link.click();
    setDownloadSuccess(true);
    setTimeout(() => {
      setDownloadSuccess(false);
      setSelectedAchievement(null);
      setCardImageUrl(null);
    }, 3000);
  };

  const handleShare = async () => {
    if (!cardImageUrl) return;
    try {
      const res = await fetch(cardImageUrl);
      const blob = await res.blob();
      const file = new File([blob], 'spocademy_achievement.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else {
        setShareFailedPrompt(true);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setShareFailedPrompt(true);
      }
    }
  };

  const getCategoryAchievements = (categoryKey) => {
    const all = achievements.filter(a => a.category === categoryKey);
    const unlocked = all.filter(a => unlockedIds.has(a.id));
    const locked = all.filter(a => !unlockedIds.has(a.id));
    return [...unlocked, ...locked];
  };

  if (loading) {
    return (
      <div className="ach-container">
        <div className="ach-header">
          <div className="ach-title">{t.title}</div>
        </div>
        <div className="ach-loading"><div className="ach-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="ach-container">
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div className="ach-header">
        <div className="ach-title">{t.title}</div>
        <button className="ach-lang-btn" onClick={handleLanguageToggle}>{t.lang}</button>
      </div>

      <div className="ach-scroll">
        {CATEGORIES.map(category => {
          const catAchievements = getCategoryAchievements(category.key);
          const unlockedCount = catAchievements.filter(a => unlockedIds.has(a.id)).length;
          return (
            <div key={category.key} className="ach-category">
              <div className="ach-category-header">
                <div className="ach-category-trophy" style={{ background: category.color }}>
                  <TrophyIcon size={16} white={true} />
                </div>
                <span className="ach-category-name">
                  {language === 'mr' ? category.labelMr : category.labelEn}
                </span>
                <span className="ach-category-count">
                  {unlockedCount}/{catAchievements.length}
                </span>
              </div>

              {catAchievements.length === 0 ? (
                <div className="ach-empty">{t.noAchievements}</div>
              ) : (
                <div className="ach-netflix-scroll">
                  {catAchievements.map(achievement => {
                    const isUnlocked = unlockedIds.has(achievement.id);
                    const cat = CATEGORIES.find(c => c.key === achievement.category);
                    const title = achievement.title?.[language] || achievement.title?.mr || achievement.title?.en || '';
                    return (
                      <div
                        key={achievement.id}
                        className={`ach-card ${isUnlocked ? 'unlocked' : 'locked'}`}
                        onClick={() => handleAchievementTap(achievement)}
                      >
                        <div className="ach-trophy-wrap"
                          style={{ background: isUnlocked ? cat?.color : '#ccc' }}>
                          <TrophyIcon size={32} white={true} />
                        </div>
                        <div className="ach-card-title">{title}</div>
                        {isUnlocked && (
                          <div className="ach-card-unlocked" style={{ color: cat?.color }}>
                            {t.unlocked}
                          </div>
                        )}
                        {!isUnlocked && (
                          <div className="ach-card-target">
                            <img src="/lock.svg" alt="locked" width={14} height={14}
                              style={{ opacity: 0.4 }} />
                            <span className="ach-card-target-text">
                              {language === 'mr'
                                ? `${toMarathiDigits(achievement.criteriaValue)} ${CRITERIA_UNIT_MR[achievement.criteriaType] || ''}`
                                : `${achievement.criteriaValue} ${CRITERIA_UNIT_EN[achievement.criteriaType] || ''}`}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedAchievement && (
        <div className="ach-overlay" onClick={() => {
          if (!generating) {
            setSelectedAchievement(null);
            setCardImageUrl(null);
          }
        }}>
          <div className="ach-popup" onClick={e => e.stopPropagation()}>
            <button className="ach-popup-close" onClick={() => {
              setSelectedAchievement(null);
              setCardImageUrl(null);
            }}>✕</button>

            {generating ? (
              <div className="ach-popup-generating">
                <div className="ach-spinner" />
                <p>{t.generating}</p>
              </div>
            ) : cardImageUrl ? (
              <>
                <img src={cardImageUrl} alt="Achievement Card" className="ach-popup-card" />
                {downloadSuccess ? (
                  <div className="ach-download-success">{t.downloadSuccess}</div>
                ) : (
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      onClick={handleDownload}
                      style={{
                        width: 140,
                        background: 'transparent',
                        border: '2px solid currentColor',
                        color: 'inherit',
                        borderRadius: 8,
                        padding: '10px 8px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {t.download}
                    </button>
                    <button
                      className="ach-download-btn"
                      onClick={handleShare}
                      style={{ width: 140, whiteSpace: 'nowrap' }}
                    >
                      {t.share}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888' }}>
                {language === 'mr'
                  ? 'या श्रेणीसाठी टेम्पलेट अपलोड केलेले नाही.'
                  : 'No template uploaded for this category yet.'}
              </div>
            )}
          </div>
        </div>
      )}

      {shareFailedPrompt && (
        <div className="ach-overlay" onClick={() => setShareFailedPrompt(false)}>
          <div className="ach-popup" onClick={e => e.stopPropagation()} style={{ padding: 24, textAlign: 'center' }}>
            <p>{t.shareFailed}</p>
            <button className="ach-download-btn" onClick={() => { setShareFailedPrompt(false); handleDownload(); }}>
              {t.yes}
            </button>
            <button className="ach-download-btn" onClick={() => setShareFailedPrompt(false)} style={{ marginTop: 8, opacity: 0.6 }}>
              {t.no}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementsPage;